"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import { cn } from "@repo/ui/utils.ts";
import { Overline } from "@repo/ui/overline";
import { VisualWrapper } from "@/components/common/visual/visual.component";
import { RichTextCondensed } from "@/components/common/rich-text/rich-text-condensed.component";
import type {
  TimelineHorizontalProps,
  TimelineHorizontalEntry,
} from "./timeline-horizontal.types";

function TimelineEntry({
  entry,
  isDesktop,
}: {
  entry: TimelineHorizontalEntry;
  isDesktop?: boolean;
}) {
  return (
    <>
      <div className={cn("flex flex-col gap-10", isDesktop && "w-[650px]")}>
        <div className="flex flex-col gap-3">
          {entry.year && (
            <span className="typography-component-sm-medium text-text-primary">
              {entry.year}
            </span>
          )}
          {entry.headline && (
            <h3 className="typography-mobile-heading-subtitle-1 desktop:typography-desktop-heading-subtitle-1 text-text-primary">
              {entry.headline}
            </h3>
          )}
        </div>
        {entry.body && (
          <RichTextCondensed
            richText={entry.body}
            className="text-text-secondary"
          />
        )}
      </div>
      {entry.visual && (
        <div
          className={cn(
            "relative shrink-0 overflow-hidden rounded-md",
            isDesktop ? "w-[533px] h-[380px]" : "mt-10 w-full aspect-[358/232]",
          )}
        >
          <VisualWrapper
            visual={entry.visual}
            className={isDesktop ? "h-full" : "aspect-[358/232]"}
            visualClassName="w-full h-full"
            imgClassName="object-cover rounded-md"
          />
        </div>
      )}
    </>
  );
}

export function TimelineHorizontal({
  overline,
  headline,
  entries,
  className,
}: TimelineHorizontalProps) {
  const hasHeader = overline || headline;
  const hasEntries = entries && entries.length > 0;

  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState]);

  const scrollBy = useCallback(
    (direction: "left" | "right") => {
      const el = scrollRef.current;
      if (!el) return;
      const segmentWidth = el.scrollWidth / (entries?.length || 1);
      el.scrollBy({
        left: direction === "left" ? -segmentWidth : segmentWidth,
        behavior: "smooth",
      });
    },
    [entries?.length],
  );

  if (!hasEntries) return null;

  return (
    <section
      className={cn("bg-surface-base-primary", className)}
      data-testid="timeline-horizontal"
    >
      <div className="container-grid py-16 desktop:py-28">
        <div className="col-span-full flex flex-col gap-8 desktop:gap-24">
          {/* Header */}
          {hasHeader && (
            <div className="flex flex-col gap-4 desktop:gap-6 desktop:max-w-[644px]">
              {overline && <Overline>{overline}</Overline>}
              {headline && (
                <h2 className="typography-mobile-heading-h5 desktop:typography-desktop-heading-h5 text-text-primary">
                  {headline}
                </h2>
              )}
            </div>
          )}

          {/* Content section: timeline content + date bar */}
          <div className="flex flex-col">
            {/* Mobile: render only the active entry (no stacking, no height inflation) */}
            {entries[activeIndex] && (
              <div className="flex flex-col pb-8 desktop:hidden">
                <TimelineEntry entry={entries[activeIndex]} />
              </div>
            )}

            {/* Desktop: active entry sits in flow (drives height), inactive
                entries overlay absolutely for the cross-fade so they don't
                inflate the container to the tallest slide. min-h keeps the
                design's 500px floor; the container grows only for the slide
                currently in view, so pagination shifts but short slides
                don't carry the tallest slide's whitespace. */}
            <div className="relative hidden min-h-[500px] overflow-hidden desktop:block">
              {entries.map((entry, index) => (
                <div
                  key={entry._key}
                  className={cn(
                    "flex flex-row items-start gap-[129px] transition-opacity duration-500",
                    index === activeIndex
                      ? "relative z-10 opacity-100"
                      : "absolute inset-x-0 top-0 opacity-0 pointer-events-none",
                  )}
                  id={`timeline-panel-${index}`}
                  role="tabpanel"
                  aria-labelledby={`timeline-tab-${index}`}
                  aria-hidden={index !== activeIndex}
                >
                  <TimelineEntry entry={entry} isDesktop />
                </div>
              ))}
            </div>

            {/* Date navigation bar */}
            <div className="relative">
              {/* Left arrow */}
              <button
                type="button"
                onClick={() => {
                  if (canScrollLeft) scrollBy("left");
                  else if (activeIndex > 0) setActiveIndex(activeIndex - 1);
                }}
                className="absolute left-0 top-0 z-10 flex cursor-pointer items-center justify-center rounded-full bg-surface-base-secondary p-3"
                aria-label="Previous timeline entry"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className="text-text-primary"
                >
                  <path
                    d="M12.5 15L7.5 10L12.5 5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {/* Right arrow */}
              <button
                type="button"
                onClick={() => {
                  if (canScrollRight) scrollBy("right");
                  else if (activeIndex < entries.length - 1)
                    setActiveIndex(activeIndex + 1);
                }}
                className="absolute right-0 top-0 z-10 flex cursor-pointer items-center justify-center rounded-full bg-surface-base-secondary p-3"
                aria-label="Next timeline entry"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className="text-text-primary"
                >
                  <path
                    d="M7.5 5L12.5 10L7.5 15"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {/* Scrollable date segments */}
              <div
                ref={scrollRef}
                role="tablist"
                aria-label="Timeline navigation"
                className="flex overflow-x-auto gap-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {entries.map((entry, index) => (
                  <button
                    key={entry._key}
                    id={`timeline-tab-${index}`}
                    type="button"
                    role="tab"
                    aria-selected={index === activeIndex}
                    aria-controls={`timeline-panel-${index}`}
                    onClick={() => setActiveIndex(index)}
                    className={cn(
                      "flex-1 min-w-[120px] h-[68px] cursor-pointer border-b border-solid pb-4 pt-0 text-left transition-colors",
                      index === activeIndex
                        ? "border-text-primary text-text-primary typography-component-sm-regular"
                        : "border-border-disabled text-text-disabled typography-component-sm-regular",
                    )}
                  >
                    {entry.year || `Entry ${index + 1}`}
                  </button>
                ))}
              </div>
            </div>
            {/* end content section wrapper */}
          </div>
        </div>
      </div>
    </section>
  );
}
